import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Input, Modal, Progress, Space, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ApiError } from '../../api/types';
import { useHalloweenUsers, useHalloweenSpins, useHalloweenRewards } from './queries';
import { HalloweenReward, HalloweenSpin, HalloweenUserProgress } from './types';
import { formatDateTime } from '../../utils/format';

const timezone = 'UTC';
const number = (value: number) => value.toLocaleString();
const errorText = (error: unknown) => error instanceof ApiError
  ? `${error.code ? `[${error.code}] ` : ''}${error.message}`
  : error instanceof Error ? error.message : 'Request failed';
const optionalNumber = (value: number | undefined) => value === undefined ? '—' : number(value);

function HistoryModal({ user, onClose }: { user: HalloweenUserProgress; onClose: () => void }) {
  const [tab, setTab] = useState('spins');
  const [spinPage, setSpinPage] = useState(1);
  const [spinSize, setSpinSize] = useState(20);
  const [rewardPage, setRewardPage] = useState(1);
  const [rewardSize, setRewardSize] = useState(20);
  const spins = useHalloweenSpins(user.user_id, spinPage, spinSize, tab === 'spins');
  const rewards = useHalloweenRewards(user.user_id, rewardPage, rewardSize, tab === 'rewards');
  const spinColumns: ColumnsType<HalloweenSpin> = [
    { title: 'Spin ID', dataIndex: 'id' },
    { title: 'Time', dataIndex: 'created_at', render: formatDateTime },
    { title: 'Slot', dataIndex: 'slot_index' },
    { title: 'Segment', render: (_, row) => optionalNumber(row.result?.segment) },
    { title: 'Candy awarded', dataIndex: 'points_awarded', render: number },
    { title: 'Tickets after', render: (_, row) => optionalNumber(row.result?.tickets_remaining) },
    { title: 'Completed level after', render: (_, row) => optionalNumber(row.result?.current_level) },
    { title: 'Cumulative candy after', render: (_, row) => optionalNumber(row.result?.level_points) },
    { title: 'New levels', render: (_, row) => row.result?.newly_reached_levels?.join(', ') || '—' },
    { title: 'Request ID', dataIndex: 'request_id' },
  ];
  const rewardColumns: ColumnsType<HalloweenReward> = [
    { title: 'Time claimed', dataIndex: 'created_at', render: formatDateTime },
    { title: 'Level', dataIndex: 'level' },
    { title: 'Gold awarded', dataIndex: 'gold_amount', render: number },
    { title: 'Spin ID', dataIndex: 'spin_id', render: (value: string | null) => value || '—' },
  ];
  return <Modal title={`Halloween 2026 — ${user.username} (#${user.user_id})`} open width={1200} footer={null} onCancel={onClose}>
    <Descriptions size="small" column={3} items={[
      { key: 'tickets', label: 'Tickets', children: number(user.tickets) },
      { key: 'level', label: 'Completed level', children: number(user.current_level) },
      { key: 'candy', label: 'Cumulative candy', children: number(user.level_points) },
      { key: 'next', label: 'Candy to next level', children: user.completed ? 'Completed' : number(user.points_to_next_level) },
      { key: 'ticket-points', label: 'Ticket points', children: number(user.ticket_points) },
      { key: 'next-ticket', label: 'Points to next ticket', children: number(user.points_to_next_ticket) },
      { key: 'start-gift', label: 'Starter tickets granted', children: user.start_tickets_granted ? 'Yes' : 'No' },
      { key: 'gift', label: 'Gift acknowledged', children: user.has_received_gift ? 'Yes' : 'No' },
      { key: 'completed', label: 'Completed at', children: formatDateTime(user.completed_at) },
    ]} />
    <Typography.Paragraph type="secondary" className="mt-4">Times use {timezone}. Rewards show claimed milestone rewards.</Typography.Paragraph>
    <Tabs activeKey={tab} onChange={setTab} items={[
      { key: 'spins', label: 'Spinning history', children: <>
        {spins.error && <Alert type="error" showIcon message={errorText(spins.error)} className="mb-4" />}
        <Button className="mb-4" loading={spins.isFetching} onClick={() => spins.refetch()}>Refresh spins</Button>
        <Table rowKey="id" columns={spinColumns} dataSource={spins.data?.data || []} loading={spins.isLoading} scroll={{ x: 1500 }} pagination={{ current: spinPage, pageSize: spinSize, total: spins.data?.meta.total || 0, showSizeChanger: true, pageSizeOptions: [20, 50, 100], onChange: (page, size) => { setSpinPage(size !== spinSize ? 1 : page); setSpinSize(size); } }} />
      </> },
      { key: 'rewards', label: 'Reward history', children: <>
        {rewards.error && <Alert type="error" showIcon message={errorText(rewards.error)} className="mb-4" />}
        <Button className="mb-4" loading={rewards.isFetching} onClick={() => rewards.refetch()}>Refresh rewards</Button>
        <Table rowKey="level" columns={rewardColumns} dataSource={rewards.data?.data || []} loading={rewards.isLoading} pagination={{ current: rewardPage, pageSize: rewardSize, total: rewards.data?.meta.total || 0, showSizeChanger: true, pageSizeOptions: [20, 50, 100], onChange: (page, size) => { setRewardPage(size !== rewardSize ? 1 : page); setRewardSize(size); } }} />
      </> },
    ]} />
  </Modal>;
}

export const Halloween2026Tab: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [selected, setSelected] = useState<HalloweenUserProgress | null>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => { setDebouncedSearch(search.trim()); setPage(1); }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);
  const users = useHalloweenUsers({ page, per_page: perPage, search: debouncedSearch || undefined });
  const columns: ColumnsType<HalloweenUserProgress> = [
    { title: 'User ID', dataIndex: 'user_id', render: (value: string) => <Link to={`/users/${value}`}>{value}</Link> },
    { title: 'Full name', dataIndex: 'full_name', render: (value: string | null, row) => value ? <Link to={`/users/${row.user_id}`}>{value}</Link> : '—' },
    { title: 'Tickets', dataIndex: 'tickets', render: number },
    { title: 'Completed level', dataIndex: 'current_level', render: number },
    { title: 'Cumulative candy', dataIndex: 'level_points', render: number },
    { title: 'Level progress', width: 230, render: (_, row) => <>
      <Progress size="small" percent={row.completed ? 100 : row.level_required_points ? Math.round(row.level_progress_points * 100 / row.level_required_points) : 0} />
      {row.completed ? <Tag color="green">Completed</Tag> : <span>Level {row.next_level}: {number(row.level_progress_points)} / {number(row.level_required_points)}</span>}
    </> },
    { title: 'Candy to next level', render: (_, row) => row.completed ? '—' : number(row.points_to_next_level) },
    { title: 'Next level threshold', render: (_, row) => row.next_level_points === null ? '—' : number(row.next_level_points) },
    { title: 'Spins', dataIndex: 'spin_count', render: number },
    { title: 'Rewards claimed', dataIndex: 'reward_count', render: number },
    { title: 'Gold claimed', dataIndex: 'reward_gold', render: number },
    { title: 'Updated', dataIndex: 'updated_at', render: formatDateTime },
    { title: 'Actions', fixed: 'right', render: (_, row) => <Button type="link" onClick={() => setSelected(row)}>History</Button> },
  ];
  return <div>
    <Space wrap className="mb-4">
      <Input aria-label="Search Halloween users" placeholder="User ID, username, or full name" allowClear value={search} onChange={e => setSearch(e.target.value)} style={{ width: 320 }} />
      <Button loading={users.isFetching} onClick={() => users.refetch()}>Refresh</Button>
    </Space>
    <Typography.Paragraph type="secondary">Users with saved Halloween progress, including when the event is disabled or ended. Level counts completed milestones; candy is cumulative. Times use {timezone}.</Typography.Paragraph>
    {users.error && <Alert type="error" showIcon message={errorText(users.error)} className="mb-4" />}
    <Table rowKey="user_id" columns={columns} dataSource={users.data?.data || []} loading={users.isLoading} scroll={{ x: 2100 }} pagination={{ current: page, pageSize: perPage, total: users.data?.meta.total || 0, showSizeChanger: true, pageSizeOptions: [20, 50, 100], showTotal: total => `${total} users`, onChange: (nextPage, size) => { setPage(size !== perPage ? 1 : nextPage); setPerPage(size); } }} />
    {selected && <HistoryModal key={selected.user_id} user={selected} onClose={() => setSelected(null)} />}
  </div>;
};
